import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { KM, REVIEWS } from "../../data/km";

const pad = (n) => String(n).padStart(2, "0");

export default function Reviews() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const secRef = useRef(null);
  const total = REVIEWS.length;
  const [expanded, setExpanded] = useState(false);
  const touchX = useRef(null);
  const go = (d) => { setExpanded(false); setI((p) => (p + d + total) % total); };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
  };

  // Always begin on review 01 each time the section scrolls into view; only auto-advance while visible
  useEffect(() => {
    const el = secRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      setVisible(e.isIntersecting);
      if (!e.isIntersecting) setI(0);
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !visible || expanded) return;
    const t = setInterval(() => go(1), 9000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, visible, expanded, total, i]);

  return (
    <section
      id="reviews"
      ref={secRef}
      className="py-14 sm:py-24 md:py-32 lg:py-36 px-6 md:px-12 lg:px-16 bg-white scroll-mt-[84px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="km-reviews"
    >
      <div className="max-w-[880px] mx-auto reveal">
        <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-4 sm:mb-6 text-[#2C4A3B] text-center">Testimonials</p>
        <h2 className="km-serif text-[40px] leading-[1.1] sm:leading-none sm:text-6xl lg:text-7xl text-[#141414] text-center">Kind <em>words</em>, fine <em>folks</em></h2>
        <span className="block w-14 h-px bg-[#141414] mx-auto my-7 sm:my-12" />

        {/* All reviews share one grid cell so the box keeps the tallest height and arrows never jump */}
        <div className="grid items-center touch-pan-y" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="review-swipe">
          {REVIEWS.map((rv, k) => (
            <blockquote
              key={k}
              aria-hidden={k !== i}
              className={`[grid-area:1/1] text-center transition-opacity duration-500 ${k === i ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              {...(k === i ? { "data-testid": "review-quote" } : {})}
            >
              <p className={`km-serif italic text-[19px] leading-[1.6] sm:text-[24px] lg:text-[27px] lg:leading-[1.65] text-[#141414] ${expanded && k === i ? "" : "max-sm:line-clamp-4"}`}>{rv.quote}</p>
              {k === i && (
                <button type="button" onClick={() => setExpanded(!expanded)} className="sm:hidden km-eyebrow text-[10px] text-[#141414] mt-1 py-3.5" aria-expanded={expanded} data-testid="review-read-more"><span className="border-b border-[#141414] pb-0.5">{expanded ? "Read less" : "Read more"}</span></button>
              )}
              <footer className="km-eyebrow text-[11px] lg:text-[12.5px] mt-5 sm:mt-8 text-black" {...(k === i ? { "data-testid": "review-author" } : {})}>{rv.author}</footer>
            </blockquote>
          ))}
        </div>

        <div className="flex items-center justify-center gap-6 mt-8 sm:mt-12">
          <button onClick={() => go(-1)} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-[#141414] flex items-center justify-center hover:bg-[#141414] hover:text-white transition-colors" aria-label="Previous review" data-testid="review-prev">
            <ChevronLeft size={18} strokeWidth={1.2} />
          </button>
          <span className="flex items-center gap-3 km-eyebrow text-[11px] lg:text-[12.5px] text-[#141414]" data-testid="review-counter">
            <span className="w-6 h-px bg-[#141414]" />{pad(i + 1)} / {pad(total)}<span className="w-6 h-px bg-[#141414]" />
          </span>
          <button onClick={() => go(1)} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-[#141414] flex items-center justify-center hover:bg-[#141414] hover:text-white transition-colors" aria-label="Next review" data-testid="review-next">
            <ChevronRight size={18} strokeWidth={1.2} />
          </button>
        </div>

        <a href={KM.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="group mt-8 sm:mt-10 mx-auto w-fit flex items-center gap-3 border border-[#E2E2E2] hover:border-[#141414] rounded-full pl-3 pr-5 py-2.5 transition-colors" data-testid="google-reviews-badge">
          <svg viewBox="0 0 48 48" className="w-5 h-5 shrink-0" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          <span className="km-serif text-[20px] leading-none text-[#141414]" data-testid="google-rating">{KM.googleRating}</span>
          <span className="flex text-[#FBBC04]" aria-label={`${KM.googleRating} out of 5 stars`}>{[0, 1, 2, 3, 4].map((n) => <Star key={n} size={13} fill="currentColor" strokeWidth={0} />)}</span>
          <span className="km-eyebrow text-[10px] sm:text-[11px] text-[#141414]" data-testid="google-review-count">{KM.googleReviewCount} Google reviews</span>
        </a>
      </div>
    </section>
  );
}
